import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Search, Home, Menu, X, ChevronDown, ArrowRight,
  AlignJustify, Lock, Settings, Wrench, MonitorSmartphone, Settings2, MessageCircle,
  ShieldCheck, Database, Camera, Cloud, Wifi, Plane, Globe, LayoutGrid, Key,
  Monitor, Box, Activity, BarChart3, Headphones, LifeBuoy, Workflow, Share2,
  BookOpen, FileText, Library,
  Laptop, Smartphone, Network, Cable, BellRing, Server, HardDrive, Package,
  GraduationCap, Briefcase, Award, Cpu, MonitorCog, Code2, Brain,
  TrendingUp, BookOpenCheck, Trophy, Users, Inbox, MessageSquare, CalendarClock, Target,
} from 'lucide-react';
import LanguageSelector from './LanguageSelector';
import CurrencySelector from './CurrencySelector';
import RegionSelector from './RegionSelector';
import ZheltaLogo from './ZheltaLogo';
import UnifiedSearch from './UnifiedSearch';
import { useI18n } from '../context/I18nContext';
import { useCurrency, appendCurrency } from '../context/CurrencyContext';
import { MAIN_MENU } from '../data/mainNav';
import { trackNavClick } from '../utils/trackNavClick';

const ICONS = {
  AlignJustify, Lock, Settings, Wrench, MonitorSmartphone, Settings2, MessageCircle,
  ShieldCheck, Database, Camera, Cloud, Wifi, Plane, Globe, LayoutGrid, Key,
  Monitor, Box, Activity, BarChart3, Headphones, LifeBuoy, Workflow, Share2,
  BookOpen, FileText, Library,
  Laptop, Smartphone, Network, Cable, BellRing, Server, HardDrive, Package,
  GraduationCap, Briefcase, Award, Cpu, MonitorCog, Code2, Brain,
  TrendingUp, BookOpenCheck, Trophy, Users, Inbox, MessageSquare, CalendarClock, Target,
};

/* Internal vs external link wrapper. Shop URLs need ?currency=XXX appended. */
function SmartLink({ to, isShop, currency, onClick, children, className, testId, style }) {
  if (isShop || /^https?:\/\//i.test(to)) {
    return (
      <a
        href={isShop ? appendCurrency(to, currency) : to}
        onClick={onClick}
        className={className}
        style={style}
        data-testid={testId}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    );
  }
  // Pure homepage in-page anchor (`/#contact`, `/#about`) — use native <a>
  // so it scrolls within the page without re-running the SPA route.
  if (to.startsWith('/#') || to.startsWith('#')) {
    return (
      <a href={to} onClick={onClick} className={className} style={style} data-testid={testId}>
        {children}
      </a>
    );
  }
  // Cross-page hash (e.g. /about#our-story) → soft SPA navigation; the target
  // page reads `useLocation().hash` and scrolls to the section.
  return (
    <Link to={to} onClick={onClick} className={className} style={style} data-testid={testId}>
      {children}
    </Link>
  );
}

function MegaMenu({ menuKey, menu, onNavigate, currency }) {
  const groups = menu.groups;
  const f = menu.featured;
  const ping = (slug, label) => trackNavClick({ slug: `main:${menuKey}:${slug}`, label });
  return (
    <div className="relative">
      <div
        className="absolute left-1/2 -translate-x-1/2 w-3 h-3 rotate-45 bg-white border-l border-t border-gray-100"
        style={{ top: '-6px' }}
        aria-hidden
      />
      <div className="relative rounded-2xl bg-white shadow-[0_16px_50px_rgba(0,0,0,0.08)] border border-zinc-100 overflow-hidden">
        <div className="grid grid-cols-12 gap-6 p-7">
          <div className="col-span-8 grid grid-cols-2 gap-x-8 gap-y-6">
            {groups.map((g) => {
              const Head = ICONS[g.icon] || AlignJustify;
              return (
                <div key={g.heading}>
                  <div className="flex items-center gap-2 mb-3">
                    <Head className="w-3.5 h-3.5 text-[#c9a227]" strokeWidth={2.2} />
                    <span className="text-[11px] font-bold tracking-[0.18em] text-gray-500">{g.heading}</span>
                  </div>
                  <ul className="space-y-2">
                    {g.items.map((it) => {
                      const Icon = ICONS[it.icon] || MessageCircle;
                      return (
                        <li key={it.label}>
                          <SmartLink
                            to={it.href}
                            isShop={it.isShop}
                            currency={currency}
                            onClick={() => {
                              ping(`item:${it.href}`, it.label);
                              onNavigate && onNavigate();
                            }}
                            testId={`main-nav-item-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                            className="flex items-center gap-2.5 text-[14px] text-gray-800 hover:text-[#c9a227] transition-colors group"
                          >
                            <Icon className="w-4 h-4 text-gray-400 group-hover:text-[#c9a227]" strokeWidth={1.75} />
                            <span>{it.label}</span>
                          </SmartLink>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>

          <div className="col-span-4">
            <div className="rounded-xl bg-gradient-to-br from-[#fff8e7] to-[#fef3c7] border border-amber-200 p-5">
              <div className="inline-block bg-[#c9a227] text-white text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded mb-3">
                {f.label || 'FEATURED'}
              </div>
              <h4 className="text-[15px] font-bold text-gray-900 mb-2">{f.title}</h4>
              <p className="text-[13px] text-gray-600 leading-relaxed mb-3">{f.body}</p>
              <ul className="space-y-1.5 mb-4">
                {f.bullets.map((b) => (
                  <li key={b} className="flex items-center gap-2 text-[12px] text-gray-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#c9a227]" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <SmartLink
                to={f.ctaHref || '/'}
                isShop={f.ctaShop}
                currency={currency}
                onClick={() => {
                  ping('featured-cta', f.cta);
                  onNavigate && onNavigate();
                }}
                testId={`main-nav-featured-cta-${(f.cta || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-white text-[13px] font-semibold hover:brightness-105 transition-all"
                style={{ background: 'linear-gradient(90deg, #ffb347 0%, #f5a623 50%, #ff8c1a 100%)' }}
              >
                {f.cta}
                <ArrowRight className="w-3.5 h-3.5" />
              </SmartLink>
            </div>
          </div>
        </div>

        {menu.footer && menu.footer.length > 0 && (
          <div className="border-t border-gray-100 bg-gray-50 px-7 py-3 flex items-center justify-end gap-5">
            {menu.footer.map((link) => (
              <SmartLink
                key={link.label}
                to={link.href}
                isShop={link.isShop}
                currency={currency}
                onClick={() => {
                  ping(`footer:${link.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`, link.label);
                  onNavigate && onNavigate();
                }}
                testId={`main-nav-footer-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className="text-[13px] text-gray-600 hover:text-[#c9a227] font-medium transition-colors"
              >
                {link.label}
              </SmartLink>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function NavItem({ label, icon: Icon, to, menuKey, hasMenu, active, isOpen, isShop, currency, testId, onOpen, onClose }) {
  return (
    <div
      className="relative"
      onMouseEnter={() => hasMenu && onOpen(menuKey)}
      onMouseLeave={() => hasMenu && onClose()}
    >
      <SmartLink
        to={to}
        isShop={isShop}
        currency={currency}
        onClick={() => trackNavClick({ slug: `main:${menuKey || 'top'}:nav`, label })}
        testId={testId}
        className={`flex items-center gap-2 px-4 py-2 text-[13px] font-semibold uppercase tracking-wider transition-colors ${
          active || isOpen
            ? 'text-[#f5a623]'
            : 'text-zinc-700 hover:text-[#f5a623]'
        }`}
      >
        {Icon && <Icon className="w-4 h-4" />}
        <span>{label}</span>
        {hasMenu && (
          <ChevronDown
            className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180 text-zinc-900' : 'text-zinc-400'}`}
            strokeWidth={2.5}
          />
        )}
      </SmartLink>
    </div>
  );
}

export default function Header() {
  const { t } = useI18n();
  const { currency } = useCurrency();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openKey, setOpenKey] = useState(null);
  const closeTimer = React.useRef(null);
  const location = useLocation();
  const pathname = location.pathname;
  const closeMobile = () => setMobileOpen(false);

  const openMenu = (key) => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpenKey(key);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenKey(null), 150);
  };
  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const closeNow = () => {
    cancelClose();
    setOpenKey(null);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Global ⌘K / Ctrl+K shortcut to open unified search.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Close any open mega-menu on route change
  useEffect(() => {
    setOpenKey(null);
  }, [pathname]);

  const isHome = pathname === '/';
  const isSystems = pathname.startsWith('/systems');
  const isAcademy = pathname.startsWith('/academy');

  const openMenuObj = openKey ? MAIN_MENU[openKey] : null;

  return (
    <header className={`sticky top-0 z-50 w-full ${scrolled ? 'shadow-sm' : ''}`}>
      {/* Utility bar */}
      <div className="bg-white border-b border-zinc-100">
        <div className="zh-container flex items-center justify-end gap-3 py-2 flex-wrap">
          <RegionSelector />
          <CurrencySelector />
          <LanguageSelector />
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            data-testid="header-search-trigger"
            className="group flex items-center gap-2 w-full sm:w-56 md:w-72 pl-4 pr-2 py-1.5 rounded-full border border-amber-200/80 bg-white hover:border-amber-400 hover:shadow-sm text-sm text-gray-500 transition-colors"
            title="Search across Systems · Services · Academy"
          >
            <Search className="w-4 h-4 text-gray-500 group-hover:text-[#c9a227]" />
            <span className="flex-1 text-left text-[13px]">{t.header.search_placeholder || 'Search…'}</span>
            <kbd className="hidden md:inline-flex items-center text-[10px] font-mono px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 border border-gray-200">⌘K</kbd>
          </button>
        </div>
      </div>

      {/* Main nav bar — DARK */}
      <div className="bg-white text-zinc-900 border-b border-zinc-200 relative">
        <div className="zh-container flex items-center h-[78px] gap-6">
          <Link
            to="/"
            data-testid="main-nav-logo"
            onClick={() => {
              if (window.location.pathname === '/') {
                window.history.replaceState(null, '', '/');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
            className="flex items-center gap-2.5 shrink-0"
          >
            <ZheltaLogo size={40} />
            <span className="text-[22px] font-extrabold tracking-[0.18em]">ZHELTA</span>
          </Link>

          <nav className="hidden lg:flex flex-1 items-center justify-center" onMouseLeave={scheduleClose}>
            <NavItem label={t.nav.home} icon={Home} to="/" active={isHome} menuKey="home" testId="main-nav-home" currency={currency} onOpen={closeNow} onClose={scheduleClose} />
            <NavItem label={t.nav.systems} to={MAIN_MENU.systems.href} hasMenu menuKey="systems" active={isSystems} isOpen={openKey === 'systems'} testId="main-nav-systems" currency={currency} onOpen={openMenu} onClose={scheduleClose} />
            <NavItem label={t.nav.services} to={MAIN_MENU.services.href} hasMenu menuKey="services" isOpen={openKey === 'services'} testId="main-nav-services" currency={currency} onOpen={openMenu} onClose={scheduleClose} />
            <NavItem label={t.nav.academy} to={MAIN_MENU.academy.href} hasMenu menuKey="academy" active={isAcademy} isOpen={openKey === 'academy'} testId="main-nav-academy" currency={currency} onOpen={openMenu} onClose={scheduleClose} />
            <NavItem label={t.nav.support} to={MAIN_MENU.support.href} hasMenu menuKey="support" isOpen={openKey === 'support'} testId="main-nav-support" currency={currency} onOpen={openMenu} onClose={scheduleClose} />
            <NavItem label={t.nav.about} to={MAIN_MENU.about.href} hasMenu menuKey="about" isOpen={openKey === 'about'} testId="main-nav-about" currency={currency} onOpen={openMenu} onClose={scheduleClose} />
          </nav>

          <div className="lg:hidden flex-1" />

          <button
            className="lg:hidden p-2 text-zinc-900"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="main-nav-mobile-toggle"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Single mega-menu panel, centered under the main bar regardless of which item is hovered */}
        {openMenuObj && (
          <div
            className="hidden lg:block absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[min(1240px,96vw)] z-[80]"
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
            data-testid={`main-nav-mega-${openKey}`}
          >
            <MegaMenu menuKey={openKey} menu={openMenuObj} onNavigate={closeNow} currency={currency} />
          </div>
        )}

        {mobileOpen && (
          <div className="lg:hidden border-t border-zinc-200 bg-white max-h-[calc(100vh-120px)] overflow-y-auto">
            <div className="zh-container py-3 flex flex-col">
              <Link
                to="/"
                onClick={closeMobile}
                className="flex items-center gap-2 px-3 py-3 text-sm font-semibold uppercase tracking-wider text-zinc-900 hover:bg-zinc-100 rounded-md"
              >
                <Home className="w-4 h-4" /> {t.nav.home}
              </Link>
              {Object.entries(MAIN_MENU).map(([key, m]) => (
                <div key={key}>
                  <button
                    onClick={() => setMobileExpanded(mobileExpanded === key ? null : key)}
                    data-testid={`main-nav-mobile-toggle-${key}`}
                    className="w-full flex items-center justify-between px-3 py-3 text-sm font-semibold uppercase tracking-wider text-zinc-900 hover:bg-zinc-100 rounded-md"
                  >
                    <span>{t.nav[key] || m.label}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${mobileExpanded === key ? 'rotate-180' : ''}`} />
                  </button>
                  {mobileExpanded === key && (
                    <div className="pl-5 pb-2">
                      {m.groups.map((g) => (
                        <div key={g.heading} className="mb-3">
                          <div className="text-[10px] font-bold tracking-[0.18em] text-zinc-500 mb-1.5 mt-2">{g.heading}</div>
                          <ul className="space-y-1.5">
                            {g.items.map((it) => {
                              const Icon = ICONS[it.icon] || MessageCircle;
                              return (
                                <li key={it.label}>
                                  <SmartLink
                                    to={it.href}
                                    isShop={it.isShop}
                                    currency={currency}
                                    onClick={() => {
                                      trackNavClick({ slug: `main:${key}:item:${it.href}`, label: it.label });
                                      closeMobile();
                                    }}
                                    className="flex items-center gap-2 text-[13px] text-zinc-700 py-1 hover:text-[#f5a623]"
                                  >
                                    <Icon className="w-3.5 h-3.5 text-zinc-500" />
                                    {it.label}
                                  </SmartLink>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      ))}
                      {m.footer && m.footer.length > 0 && (
                        <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1 pb-2">
                          {m.footer.map((link) => (
                            <SmartLink
                              key={link.label}
                              to={link.href}
                              isShop={link.isShop}
                              currency={currency}
                              onClick={closeMobile}
                              className="text-[12px] text-zinc-500 hover:text-[#f5a623] font-medium"
                            >
                              {link.label}
                            </SmartLink>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <UnifiedSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
}
